import arrowRightIcon from '@raison/static/icons/arrow-right-s-line.svg';
import warningSrc from '@raison/static/icons/error-warning-line.svg';
import messageSrc from '@raison/static/icons/information-line.svg';
import {cva, RsnMessage, Skeleton} from '@raison/ui-kit';
import {VariantProps} from 'cva';
import React, {createContext, useContext} from 'react';
import {Link, To} from 'react-router';
import {invariant} from '~/library/toolbox';
import {AlertLevel} from '../model/IAlert';

export namespace AlertPane {
    export interface Props extends Pick<VariantProps<typeof cls.smallText>, 'color'> {
        linkTo: To;
        children: React.JSX.Element | string;
        level?: AlertLevel;
        icon?: string;
        messageSummary?: React.JSX.Element | string;
    }
}

export type AlertDisplayMode = 'singular' | 'multiple-hidden' | 'multiple-visible';

export const AlertModeContext = createContext<AlertDisplayMode | null>(null);

export function useAlertMode() {
    const mode = useContext(AlertModeContext);
    invariant(mode !== null, 'AlertModeContext is missing');
    return mode;
}

export function AlertPane({
    linkTo,
    level = 'warning',
    icon = fallbackIcons[level],
    messageSummary,
    children: messageText,
    color,
}: AlertPane.Props) {
    const mode = useAlertMode();

    if (mode === 'singular') {
        return (
            <RsnMessage.Notification as={Link} to={linkTo} arrowed centered>
                {`${messageSummary}. ${messageText}`}
            </RsnMessage.Notification>
        );
    }

    let largeText: string | React.JSX.Element;
    let smallText: string | React.JSX.Element | null;
    if (messageSummary === undefined) {
        largeText = messageText;
        smallText = null;
    } else {
        largeText = messageSummary;
        smallText = messageText;
    }

    return (
        <Link className='flex items-center justify-start gap-4 p-4' to={linkTo}>
            <figure className='size-9 shrink-0 rounded-full bg-neutral-20 p-2'>
                <img src={icon} alt='' height='100%' width='100%' />
            </figure>
            <div className='mr-auto flex flex-col items-start text-start'>
                <p className='fv-text-xl text-neutral-100'>{largeText}</p>
                {smallText && <p className={cls.smallText({color})}>{smallText}</p>}
            </div>
            <Arrow />
        </Link>
    );
}

function AlertPaneSkeleton() {
    const mode = useAlertMode();
    if (mode === 'singular') {
        return <Skeleton height={60} />;
    }
    return (
        <div className='flex items-center justify-start gap-4 p-4'>
            <Skeleton circle className='size-9 shrink-0'></Skeleton>
            <div className='mr-auto flex flex-col items-start gap-2 text-start'>
                <Skeleton height={14} width={80} />
                <Skeleton height={10} width={140} />
            </div>
        </div>
    );
}

AlertPaneSkeleton.displayName = 'AlertPane.Skeleton';
AlertPane.Skeleton = AlertPaneSkeleton;
AlertPane.NoopCatcher = function NoopCatcher() {
    return null;
};

const cls = {
    smallText: cva('fv-text-m', {
        variants: {
            color: {
                yellow: 'text-warning-30',
                green: 'text-success-30',
                red: 'text-danger-30',
                neutral: 'text-neutral-60',
            },
        },
        defaultVariants: {
            color: 'neutral',
        },
    }),
};

const fallbackIcons = {
    message: messageSrc,
    warning: warningSrc,
};

function Arrow() {
    return <img className='size-5 shrink-0' src={arrowRightIcon} alt='' />;
}
